import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  AirportDropDownList,
  ErrorMessageComponent,
  LocationComponent
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { Subject } from 'rxjs';
import { ViewSearchOptionsComponent } from '../../shared/components/view-search-options/view-search-options.component';
import { CarsWidgetBaseComponent } from '../cars-widget-base-component/cars-widget-base.component';
import { CarsWidgetFormType } from '../models/cars-widget-form.interfaces';
import { CarsWidget } from '../models/cars-widget.interface';
import { CarsFormLifecycleService } from '../services/cars-form-lifecycle.service';
import { CarsFormSubmissionService } from '../services/cars-form-submission.service';
import { CarsLocationService } from '../services/cars-location.service';
@Component({
  selector: 'idp-car-widget',
  standalone: true,
  imports: [
    CommonModule,
    LocationComponent,
    ReactiveFormsModule,
    ViewSearchOptionsComponent,
    ErrorMessageComponent
  ],
  templateUrl: './cars-widget.component.html',
  styleUrls: ['./cars-widget.component.scss']
})
export class CarsWidgetComponent
  extends CarsWidgetBaseComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() carsWidgetForm!: FormGroup<CarsWidgetFormType>;
  @Output() formSubmitted = new EventEmitter<void>();
  @Output() override expandEmit = new EventEmitter<boolean>();
  @Input() content = {} as CarsWidget;
  @Input() locationSelected: AirportDropDownList = { code: '', name: '' };
  @Input() viewSearchOptionExpanded = false;
  @Input() resetErrors = false;
  @Output() openModalEmit = new EventEmitter<boolean>();
  @Output() analyticsEvent = new EventEmitter<HTMLElement>();

  @ViewChild(LocationComponent) locationComponent!: LocationComponent;

  destroy$ = new Subject<void>();
  private formLifecycleService = inject(CarsFormLifecycleService);
  private formSubmissionService = inject(CarsFormSubmissionService);
  private locationService = inject(CarsLocationService);

  ngAfterViewInit(): void {
    if (this.getRequiredElements(this.content)) {
      this.initializeComponents();
      this.resetDatePickers();
      this.updatePickupDateDisplay();
      this.updateDropoffDateDisplay();
      this.formLifecycleService.setupFormValueChanges(
        this.carsWidgetForm,
        this.content,
        () => this.isSubmitted,
        this.destroy$,
        (errors) => this.getError(errors, this.carsWidgetForm)
      );
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.handleResetErrors(changes);
    this.handleLocationChange(changes);
  }

  handleResetErrors(changes: SimpleChanges): void {
    if (changes['resetErrors'] && this.resetErrors) {
      this.clearAllErrors();
    }
  }

  handleLocationChange(changes: SimpleChanges): void {
    const locationChange = changes['locationSelected'];
    if (locationChange && this.locationSelected?.name) {
      this.locationService.updateLocationContent(
        this.content,
        this.carsWidgetForm,
        this.locationSelected
      );
      if (this.isSubmitted) {
        this.clearLocationError();
      }

      setTimeout(() => this.locationComponent?.focusField());
    }
  }

  initializeComponents(): void {
    this.initializePickupControls(this.carsWidgetForm, this.content);
    this.initializeDropoffControls(this.carsWidgetForm, this.content);
  }

  locationClicked(event: boolean): void {
    this.openModalEmit.emit(event);
  }

  processFormSubmit(event: Event): void {
    event.preventDefault();
    this.isSubmitted = true;
    this.isIntentionalSubmit = false;
    this.formLifecycleService.markFormAsTouched(this.carsWidgetForm);
    const errors = this.formSubmissionService.validateAndGetErrors(
      this.carsWidgetForm,
      this.content
    );
    this.getError(errors, this.carsWidgetForm);
    if (
      this.formSubmissionService.hasValidationErrors(
        errors,
        this.carsWidgetForm
      )
    )
      return;
    if (this.carsWidgetForm.valid) {
      this.formSubmitted.emit();
    }
  }

  formSubmit(event: Event): void {
    if (!this.isIntentionalSubmit) {
      event.preventDefault();
      return;
    }
    this.processFormSubmit(event);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
